import React, { useEffect, useMemo, useState } from 'react';
import { usePendingAgreements, setPending, clearPending } from '../../stores/agreementGateStore';
import { clearToken } from '../../stores/authStore';
import { apiPost } from '../../utils/api';
import AgreementBody from './AgreementBody';
import toast from 'react-hot-toast';

async function jsonFrom(response) {
  const text = await response.text();
  try { return text ? JSON.parse(text) : {}; } catch { return {}; }
}

export default function AgreementGateModal() {
  const pending = usePendingAgreements();
  const current = pending[0];
  const [checked, setChecked] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { setChecked({}); }, [current?.agreement_version_id]);

  const requiredIds = useMemo(() => {
    if (!current) return [];
    if (current.acknowledgment_mode === 'single') return ['__single__'];
    return (current.acknowledgment_items || []).filter((i) => i.required).map((i) => i.id);
  }, [current]);

  const canAccept = requiredIds.every((id) => checked[id]);

  if (!current) return null;

  async function onAccept() {
    setSubmitting(true);
    try {
      const accepted_items =
        current.acknowledgment_mode === 'single'
          ? []
          : Object.keys(checked).filter((k) => checked[k]);
      const resp = await apiPost('/agreements/accept', {
        agreement_version_id: current.agreement_version_id,
        accepted_items
      });
      const body = await jsonFrom(resp);
      if (resp.ok) {
        setPending(body.pending_agreements || []);
      } else if (resp.status === 409) {
        toast.error('This agreement was just updated. Please review the latest version.');
        setPending(body.pending_agreements || []);
      } else {
        toast.error(body.error || 'Could not record acceptance. Please try again.');
      }
    } catch (_e) {
      toast.error('Could not record acceptance. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  async function onLogOut() {
    setSubmitting(true);
    try {
      await apiPost('/agreements/decline', {});
    } catch (_e) { /* proceed with local logout anyway */ }
    clearToken();
    clearPending();
    toast('You declined the agreements. You can log back in at any time to review and accept.', { duration: 6000 });
    window.location.href = '/login';
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="agreement-gate-title"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60"
    >
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-3xl w-[92vw] max-h-[90vh] flex flex-col">
        <header className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 id="agreement-gate-title" className="text-xl font-semibold text-gray-900 dark:text-gray-100">{current.name}</h2>
          {pending.length > 1 && (
            <p className="text-sm text-gray-500 mt-1">1 of {pending.length}</p>
          )}
        </header>

        <div className="flex-1 overflow-y-auto px-8 py-6 bg-gray-50 dark:bg-gray-950">
          <AgreementBody
            markdown={current.body_markdown}
            className="font-serif text-[12.5px] leading-snug text-gray-800 dark:text-gray-200 [&_h1]:text-base [&_h1]:font-semibold [&_h1]:mt-4 [&_h1]:mb-2 [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-3 [&_h2]:mb-1 [&_h3]:text-[13px] [&_h3]:font-semibold [&_p]:my-2 [&_p]:text-justify [&_li]:my-0.5 [&_ul]:pl-5 [&_ol]:pl-5"
          />
        </div>

        <div className="p-4 border-t border-gray-200 dark:border-gray-700 space-y-3">
          {current.acknowledgment_mode === 'single' ? (
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!checked.__single__}
                onChange={(e) => setChecked({ __single__: e.target.checked })}
              />
              <span className="text-sm font-rasa text-gray-700 dark:text-gray-200">
                I have read and agree to the {current.name}.
              </span>
            </label>
          ) : (
            (current.acknowledgment_items || []).map((item) => (
              <label key={item.id} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!checked[item.id]}
                  onChange={(e) => setChecked((prev) => ({ ...prev, [item.id]: e.target.checked }))}
                />
                <span className="text-sm text-gray-700 dark:text-gray-200">{item.label}</span>
              </label>
            ))
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onLogOut}
              disabled={submitting}
              className="px-4 py-2 rounded border font-rasa text-base border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
            >
              Log Out
            </button>
            <button
              type="button"
              onClick={onAccept}
              disabled={!canAccept || submitting}
              className="px-4 py-2 rounded font-rasa text-base bg-green-700 text-white disabled:opacity-50"
            >
              {submitting ? 'Working…' : 'Accept'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
